import { ChangeEvent, useMemo, VFC } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';

import HandlebarFormControl from 'dashboard/components/HandlebarFormControl';
import { OptionType, ReactCreatableSelect } from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { generateJsonSchemaConfig, UsableObjectConfig } from 'dashboard/lib/workflows';
import { Trigger } from 'dashboard/models/trigger';
import { WorkflowActionMeta, WorkflowError } from 'dashboard/models/workflow';
import {
  WorkflowActionZendeskOAuthUpdateTicketV1,
  ZendeskSupportedTicketStatuses,
} from 'dashboard/models/workflow-version';
import Checkbox from 'design-system/components/Checkbox';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { JsonSchemaStringifiableTypes } from 'lib/fields';
import { ApiModel } from 'lib/interfaces/api';
import { JsonSchemaTypes } from 'lib/json-schema';

interface ParamsZendeskOAuthUpdateTicketV1Props extends ParamsBaseProps {
  externalIntegrations: WorkflowActionMeta['external-integrations'];
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionZendeskOAuthUpdateTicketV1['params'];
  trigger?: Trigger;
  usableObjects: UsableObjectConfig[];
  updateActionParams: (newParams: WorkflowActionZendeskOAuthUpdateTicketV1['params']) => void;
}

const SUPPORTED_TICKET_STATUS: Record<ZendeskSupportedTicketStatuses, string> = {
  [ZendeskSupportedTicketStatuses.DEFAULT]: 'Default',
  [ZendeskSupportedTicketStatuses.NEW]: 'New',
  [ZendeskSupportedTicketStatuses.OPEN]: 'Open',
  [ZendeskSupportedTicketStatuses.PENDING]: 'Pending',
  [ZendeskSupportedTicketStatuses.HOLD]: 'Hold',
  [ZendeskSupportedTicketStatuses.SOLVED]: 'Solved',
  [ZendeskSupportedTicketStatuses.CLOSED]: 'Closed',
};

const statusOptions: OptionType[] = Object.entries(SUPPORTED_TICKET_STATUS).map(
  ([value, label]) => ({
    value,
    label,
  }),
);

const ParamsZendeskOAuthUpdateTicketV1: VFC<ParamsZendeskOAuthUpdateTicketV1Props> = ({
  step,
  externalIntegrations,
  editable,
  errors,
  params,
  trigger,
  usableObjects,
  updateActionParams,
  updateStep,
}) => {
  const { jsonSchema, tokensToDisplayValue } = useMemo(() => {
    return generateJsonSchemaConfig({
      usableObjects,
    });
  }, [usableObjects]);

  const updateZendeskTicketParams = <
    Key extends keyof WorkflowActionZendeskOAuthUpdateTicketV1['params'],
  >(
    key: Key,
    value: WorkflowActionZendeskOAuthUpdateTicketV1['params'][Key],
  ) => {
    updateActionParams({
      ...params,
      [key]: value,
    });
  };

  const zendeskInstanceOptions = [
    ...externalIntegrations
      .filter(
        (integration) =>
          integration.type === ApiModel.ExternalIntegrationProductivityZendeskOAuth &&
          !integration.isArchived,
      )
      .map((integration) => ({
        label: integration.name,
        value: integration.token,
      })),
    { label: 'Add New Zendesk Instance', value: 'marketplace-managment-redirect' },
  ];

  const navigate = useNavigate();
  const selectZendeskInstance = (zendeskInstance: string) => {
    if (zendeskInstance === 'marketplace-managment-redirect') {
      navigate(`/dashboard/marketplace/zendesk`);
    } else {
      updateActionParams({
        ...params,
        'external-integration-id': zendeskInstance,
      });
    }
  };

  const handlePublicCheckbox = ({ target: { checked } }: ChangeEvent<HTMLInputElement>) => {
    updateZendeskTicketParams('is-public', checked);
  };

  const tagOptions = (tagParam: string[]) => {
    return tagParam.map((tag) => {
      return { label: tag, value: tag };
    });
  };

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionZendeskOAuthUpdateTicketV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel
        labelText='Zendesk Instance'
        htmlFor='params-zendesk-oauth-update-ticket__external-integration-id'
      />
      <Select
        value={params['external-integration-id']}
        onValueChange={selectZendeskInstance}
        id='params-zendesk-oauth-update-ticket__external-integration-id'
        disabled={!editable}
        hasError={hasErrorFor('external-integration-id')}
      >
        {zendeskInstanceOptions.map((opt) => (
          <Option key={opt.label} value={opt.value}>
            {opt.label}
          </Option>
        ))}
      </Select>
      {renderErrorMessageFor('external-integration-id')}
      <Spacer height={2} />
      <ParamLabel labelText='Ticket ID' htmlFor='params-zendesk-oauth-update-ticket__ticket-id' />
      <HandlebarFormControl
        size='md'
        type='input'
        value={params['ticket-id']}
        updateValue={(value: string) => {
          updateZendeskTicketParams('ticket-id', value);
        }}
        editable={editable}
        allowedTypes={[JsonSchemaTypes.String]}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        id='params-zendesk-oauth-update-ticket__ticket-id'
        hasError={hasErrorFor('ticket-id')}
      />
      {renderErrorMessageFor('ticket-id')}
      <Spacer height={2} />
      <ParamLabel
        labelText='Comment Body'
        htmlFor='params-zendesk-oauth-update-ticket__comment-body'
      />
      <HandlebarFormControl
        size='md'
        type='text-area'
        value={params['body']}
        updateValue={(value: string) => {
          updateZendeskTicketParams('body', value);
        }}
        editable={editable}
        allowedTypes={JsonSchemaStringifiableTypes}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        id='params-zendesk-oauth-update-ticket__comment-body'
        hasError={hasErrorFor('body')}
      />
      {renderErrorMessageFor('body')}
      <Spacer height={2} />
      <StepAdvancedConfigurationWithErrorHandlingPanel
        step={step}
        trigger={trigger}
        updateStep={updateStep}
        disabled={!editable}
      >
        <Spacer height={2} />
        <Checkbox
          id='params-zendesk-oauth-update-ticket__make-is-public'
          label='Make the Comment Public'
          checked={params['is-public']}
          disabled={!editable}
          onChange={handlePublicCheckbox}
        />
        <Spacer height={2} />
        <ParamLabel labelText='Status' htmlFor='params-zendesk-oauth-update-ticket__status' />
        <Select
          value={params['status']}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onValueChange={(value: WorkflowActionZendeskOAuthUpdateTicketV1['params']['status']) => {
            updateZendeskTicketParams('status', value);
          }}
          id='params-zendesk-oauth-update-ticket__status'
          disabled={!editable}
        >
          {statusOptions.map(({ label, value }) => (
            <Option key={`${label}-${value}`} value={value}>
              {label}
            </Option>
          ))}
        </Select>
        <Spacer height={2} />
        <ParamLabel labelText='Tags' htmlFor='params-zendesk-oauth-update-ticket__tags-input' />
        <ReactCreatableSelect
          size='md'
          id='params-zendesk-oauth-update-ticket__tags'
          value={params['tags']}
          disabled={!editable}
          isMulti={true}
          isSearchable={true}
          portal={true}
          onChange={(value: string[]) => {
            updateZendeskTicketParams('tags', value);
          }}
          onCreateOption={(str: string) => {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            updateZendeskTicketParams('tags', params['tags'].concat(str));
          }}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          options={tagOptions(params['tags'])}
        />
        <Spacer height={2} />
      </StepAdvancedConfigurationWithErrorHandlingPanel>
    </>
  );
};
export default ParamsZendeskOAuthUpdateTicketV1;
